// To parse this data:
//
//   import { Convert, LoginOptionsResponse } from "./file";
//
//   const loginOptionsResponse = Convert.toLoginOptionsResponse(json);
//
// These functions will throw an error if the JSON doesn't
// match the expected interface, even if the JSON is valid.

/**
 * Response object that respresents basic login options to be returned when a successful GET
 * | PUT | POST request is made through endpoint auth-configs/all-auth-options.
 *
 * Schema for the items in the central-auth login-options DynamoDB table.
 */
export interface LoginOptionsResponse {
    /**
     * The list of excluded role IDs in which users in this group are permitted to login via
     * both Standard and SSO Login regardless of the AuthenticationType.
     */
    excluded_roles?: string[];
    /**
     * The list of excluded user IDs, in which users in it are permitted to login via both
     * Standard and SSO Login regardless of the AuthenticationType.
     */
    excluded_users?: string[];
    /**
     * The Authentication Type allowed by the org/tenant.
     */
    authentication_type: AuthenticationType;
    /**
     * The exclude tenant admins, is the boolean value if enabled admins will be allowed to
     * login via both Standard and SSO Login.
     */
    exclude_tenant_admins?: boolean;
    /**
     * The exclusion type in which entities in this grouping are allowed to login via both
     * Standard and SSO Login regardless of the AuthenticationType enabled.
     */
    exclusion_types?: ExclusionType[];
    /**
     * The id of the tenant for this entry. This is the first part of the composite key
     * (followed by TenantType).
     */
    tenant_id: string;
    /**
     * The product that the tenant is using with the central auth service. This is the second
     * part of the composite key.
     */
    tenant_type: TenantType;
    [property: string]: any;
}

/**
 * The Authentication Type allowed by the org/tenant.
 */
export enum AuthenticationType {
    SsoLoginOnly = "SSO_LOGIN_ONLY",
    StandardAndSsoLogin = "STANDARD_AND_SSO_LOGIN",
}

export enum ExclusionType {
    Role = "ROLE",
    User = "USER",
}

/**
 * The product that the tenant is using with the central auth service. This is the second
 * part of the composite key.
 */
export enum TenantType {
    Apricot = "APRICOT",
    Eto = "ETO",
    None = "NONE",
}

// Converts JSON strings to/from your types
// and asserts the results of JSON.parse at runtime
export class Convert {
    public static toLoginOptionsResponse(json: string): LoginOptionsResponse {
        return cast(JSON.parse(json), r("LoginOptionsResponse"));
    }

    public static loginOptionsResponseToJson(value: LoginOptionsResponse): string {
        return JSON.stringify(uncast(value, r("LoginOptionsResponse")), null, 2);
    }
}

function invalidValue(typ: any, val: any, key: any, parent: any = ''): never {
    const prettyTyp = prettyTypeName(typ);
    const parentText = parent ? ` on ${parent}` : '';
    const keyText = key ? ` for key "${key}"` : '';
    throw Error(`Invalid value${keyText}${parentText}. Expected ${prettyTyp} but got ${JSON.stringify(val)}`);
}

function prettyTypeName(typ: any): string {
    if (Array.isArray(typ)) {
        if (typ.length === 2 && typ[0] === undefined) {
            return `an optional ${prettyTypeName(typ[1])}`;
        } else {
            return `one of [${typ.map(a => { return prettyTypeName(a); }).join(", ")}]`;
        }
    } else if (typeof typ === "object" && typ.literal !== undefined) {
        return typ.literal;
    } else {
        return typeof typ;
    }
}

function jsonToJSProps(typ: any): any {
    if (typ.jsonToJS === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.json] = { key: p.js, typ: p.typ });
        typ.jsonToJS = map;
    }
    return typ.jsonToJS;
}

function jsToJSONProps(typ: any): any {
    if (typ.jsToJSON === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.js] = { key: p.json, typ: p.typ });
        typ.jsToJSON = map;
    }
    return typ.jsToJSON;
}

function transform(val: any, typ: any, getProps: any, key: any = '', parent: any = ''): any {
    function transformPrimitive(typ: string, val: any): any {
        if (typeof typ === typeof val) return val;
        return invalidValue(typ, val, key, parent);
    }

    function transformUnion(typs: any[], val: any): any {
        // val must validate against one typ in typs
        const l = typs.length;
        for (let i = 0; i < l; i++) {
            const typ = typs[i];
            try {
                return transform(val, typ, getProps);
            } catch (_) {}
        }
        return invalidValue(typs, val, key, parent);
    }

    function transformEnum(cases: string[], val: any): any {
        if (cases.indexOf(val) !== -1) return val;
        return invalidValue(cases.map(a => { return l(a); }), val, key, parent);
    }

    function transformArray(typ: any, val: any): any {
        // val must be an array with no invalid elements
        if (!Array.isArray(val)) return invalidValue(l("array"), val, key, parent);
        return val.map(el => transform(el, typ, getProps));
    }

    function transformDate(val: any): any {
        if (val === null) {
            return null;
        }
        const d = new Date(val);
        if (isNaN(d.valueOf())) {
            return invalidValue(l("Date"), val, key, parent);
        }
        return d;
    }

    function transformObject(props: { [k: string]: any }, additional: any, val: any): any {
        if (val === null || typeof val !== "object" || Array.isArray(val)) {
            return invalidValue(l(ref || "object"), val, key, parent);
        }
        const result: any = {};
        Object.getOwnPropertyNames(props).forEach(key => {
            const prop = props[key];
            const v = Object.prototype.hasOwnProperty.call(val, key) ? val[key] : undefined;
            result[prop.key] = transform(v, prop.typ, getProps, key, ref);
        });
        Object.getOwnPropertyNames(val).forEach(key => {
            if (!Object.prototype.hasOwnProperty.call(props, key)) {
                result[key] = transform(val[key], additional, getProps, key, ref);
            }
        });
        return result;
    }

    if (typ === "any") return val;
    if (typ === null) {
        if (val === null) return val;
        return invalidValue(typ, val, key, parent);
    }
    if (typ === false) return invalidValue(typ, val, key, parent);
    let ref: any = undefined;
    while (typeof typ === "object" && typ.ref !== undefined) {
        ref = typ.ref;
        typ = typeMap[typ.ref];
    }
    if (Array.isArray(typ)) return transformEnum(typ, val);
    if (typeof typ === "object") {
        return typ.hasOwnProperty("unionMembers") ? transformUnion(typ.unionMembers, val)
            : typ.hasOwnProperty("arrayItems")    ? transformArray(typ.arrayItems, val)
            : typ.hasOwnProperty("props")         ? transformObject(getProps(typ), typ.additional, val)
            : invalidValue(typ, val, key, parent);
    }
    // Numbers can be parsed by Date but shouldn't be.
    if (typ === Date && typeof val !== "number") return transformDate(val);
    return transformPrimitive(typ, val);
}

function cast<T>(val: any, typ: any): T {
    return transform(val, typ, jsonToJSProps);
}

function uncast<T>(val: T, typ: any): any {
    return transform(val, typ, jsToJSONProps);
}

function l(typ: any) {
    return { literal: typ };
}

function a(typ: any) {
    return { arrayItems: typ };
}

function u(...typs: any[]) {
    return { unionMembers: typs };
}

function o(props: any[], additional: any) {
    return { props, additional };
}

function m(additional: any) {
    return { props: [], additional };
}

function r(name: string) {
    return { ref: name };
}

const typeMap: any = {
    "LoginOptionsResponse": o([
        { json: "excluded_roles", js: "excluded_roles", typ: u(undefined, a("")) },
        { json: "excluded_users", js: "excluded_users", typ: u(undefined, a("")) },
        { json: "authentication_type", js: "authentication_type", typ: r("AuthenticationType") },
        { json: "exclude_tenant_admins", js: "exclude_tenant_admins", typ: u(undefined, true) },
        { json: "exclusion_types", js: "exclusion_types", typ: u(undefined, a(r("ExclusionType"))) },
        { json: "tenant_id", js: "tenant_id", typ: "" },
        { json: "tenant_type", js: "tenant_type", typ: r("TenantType") },
    ], "any"),
    "AuthenticationType": [
        "SSO_LOGIN_ONLY",
        "STANDARD_AND_SSO_LOGIN",
    ],
    "ExclusionType": [
        "ROLE",
        "USER",
    ],
    "TenantType": [
        "APRICOT",
        "ETO",
        "NONE",
    ],
};
